import { create } from "zustand";
import { supabase } from "../lib/supabase";

export type CommandBarState =
  | "idle"
  | "loading_plan"
  | "showing_plan"
  | "executing"
  | "done"
  | "error";

export interface AiAction {
  id: string;
  tool: string;
  params: Record<string, unknown>;
}

export interface AiActionResult {
  actionId: string;
  tool: string;
  status: "success" | "error";
  result?: Record<string, unknown>;
  error?: string;
}

export interface AiPlan {
  summary: string;
  actions: AiAction[];
}

interface AiStoreState {
  isCommandBarOpen: boolean;
  state: CommandBarState;
  plan: AiPlan | null;
  results: AiActionResult[] | null;
  error: string | null;

  openCommandBar: () => void;
  closeCommandBar: () => void;
  toggleCommandBar: () => void;
  generatePlan: (command: string) => Promise<void>;
  executePlan: (actions: AiAction[]) => Promise<void>;
  reset: () => void;
}

export const useAiStore = create<AiStoreState>((set) => ({
  isCommandBarOpen: false,
  state: "idle",
  plan: null,
  results: null,
  error: null,

  openCommandBar: () => set({ isCommandBarOpen: true }),

  closeCommandBar: () =>
    set({
      isCommandBarOpen: false,
      state: "idle",
      plan: null,
      results: null,
      error: null,
    }),

  toggleCommandBar: () =>
    set((s) => ({
      isCommandBarOpen: !s.isCommandBarOpen,
      state: "idle",
      plan: null,
      results: null,
      error: null,
    })),

  generatePlan: async (command: string) => {
    set({ state: "loading_plan", error: null });

    const { data, error } = await supabase.functions.invoke("ai-plan", {
      body: { command },
    });

    if (error) {
      set({ state: "error", error: error.message });
      return;
    }

    if (data?.error) {
      set({ state: "error", error: data.error });
      return;
    }

    set({ state: "showing_plan", plan: data.plan });
  },

  executePlan: async (actions: AiAction[]) => {
    set({ state: "executing" });

    const { data, error } = await supabase.functions.invoke("ai-execute", {
      body: { actions },
    });

    if (error) {
      set({ state: "error", error: error.message });
      return;
    }

    if (data?.error) {
      set({ state: "error", error: data.error });
      return;
    }

    set({ state: "done", results: data.results });
  },

  reset: () =>
    set({
      state: "idle",
      plan: null,
      results: null,
      error: null,
    }),
}));
